import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Language from '../../../../types/language/Language';
import LanguageWriteDTO from '../../../../types/language/LanguageWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { ExportService } from '../../services/export.service';
import { LanguageService } from '../../services/language.service';
import {
    CreateLanguageDialogComponent
} from '../shared/languages/create-language-dialog/create-language-dialog.component';
import {
    UpdateLanguageDialogComponent
} from '../shared/languages/update-language-dialog/update-language-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-languages',
    templateUrl: './languages.component.html',
    styleUrls: [ './languages.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateLanguageDialogComponent,
        UpdateLanguageDialogComponent,
        InputText,
        RouterLink,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip
    ]
})
export class LanguagesComponent implements OnInit {
    public search = '';
    public languages: Language[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateLanguageDialog = false;
    public displayUpdateLanguageDialog = false;
    public selectedLanguage: LanguageWriteDTO = new LanguageWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(private languageService: LanguageService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private exportService: ExportService) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadLanguages();

        // Debounce the search input to prevent too many requests
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset to the first page on new search
            this.loadLanguages();
        });
    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadLanguages();
    }

    /**
     * Handles the change of the search input.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Shows the edit language dialog.
     */
    public editLanguage(language: Language): void {
        this.selectedLanguage = new LanguageWriteDTO(language);
        this.displayUpdateLanguageDialog = true;
    }

    /**
     * Handles the deletion of a new language. Confirms the deletion with the user.
     */
    public deleteLanguage(language: Language): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the language ' + language.name + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.languageService.deleteLanguage(language.id).then(() => {
                    this.loadLanguages();
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Language deleted', life: 3000 });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the create language dialog.
     */
    public openCreateLanguageDialog(): void {
        this.displayCreateLanguageDialog = true;
    }

    /**
     * Reloads the languages.
     */
    public reloadLanguages(): void {
        this.loadLanguages();
    }

    /**
     * Exports the languages to a file.
     */
    public exportLanguages() {
        this.exportService.exportLanguages()
            .then(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Languages exported',
                    life: 3000
                });
            }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        });
    }

    /**
     * Loads the languages from the server.
     */
    private loadLanguages(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.languageService
            .getLanguages(search, this.currentPage, size)
            .then((result: Page<Language>) => {
                this.languages = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

}
